import axios, { AxiosError } from 'axios';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import {
  User,
  Room,
  Sensor,
  SensorReading,
  Alert,
  RoomDetail,
  SensorDetail,
  LoginRequest,
  TokenResponse,
  RoomCreate,
  SensorCreate,
  ActivationCode,
  DeviceHealth,
  CodeGenerateRequest,
  HeartbeatResult,
  FirmwareVersion,
  FlashLog,
  DeviceLifecycleEvent,
  DeviceReplaceRequest,
  DeviceRelocateRequest,
  DeviceSwapRequest,
  DeviceDecommissionRequest,
  DeviceReactivateRequest,
  DataMigrationRequest,
  SensorHistory,
  ComplianceReport,
  PendingActivation,
  AuditLogEntry,
  AlertAcknowledgeData,
  ChainStatus,
  ChainVerifyResult,
  GapDetectedItem,
  GapDeclaration,
  GapDeclarationCreate,
  GapDeclareRequest,
  GapApproveRequest,
  GapDismissRequest,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : 'http://localhost:8000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = safeGetItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 6.5: Token refresh state
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

// Response interceptor with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const isLoginPage = window.location.pathname === '/login';
      const isAuthCall = originalRequest?.url?.includes('/auth/me') ||
                         originalRequest?.url?.includes('/auth/login') ||
                         originalRequest?.url?.includes('/auth/refresh');

      // Skip refresh for auth endpoints and login page
      if (isLoginPage || isAuthCall) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: any) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = safeGetItem('refresh_token');
      if (!refreshToken) {
        // No refresh token — logout
        isRefreshing = false;
        safeRemoveItem('access_token');
        safeRemoveItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        safeSetItem('access_token', data.access_token);
        safeSetItem('refresh_token', data.refresh_token);

        // Retry queued requests with new token
        processQueue(null, data.access_token);

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — logout
        processQueue(refreshError, null);
        safeRemoveItem('access_token');
        safeRemoveItem('refresh_token');
        safeRemoveItem('current_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    // Build URL with tenant_slug (preferred) or tenant_id (legacy) query parameter
    let url = `${API_BASE_URL}/auth/login`;
    const queryParams: string[] = [];
    if (credentials.tenant_slug) {
      queryParams.push(`tenant_slug=${encodeURIComponent(credentials.tenant_slug)}`);
    } else if (credentials.tenant_id !== undefined && credentials.tenant_id !== null) {
      queryParams.push(`tenant_id=${credentials.tenant_id}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    // Include device token for "remember this device" 2FA bypass
    const deviceToken = safeGetItem('device_token');
    if (deviceToken) {
      headers['X-Device-Token'] = deviceToken;
    }

    const response = await axios.post(url, formData, { headers });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  logout() {
    // Call backend to blacklist tokens (best-effort, don't block on failure)
    const refreshToken = safeGetItem('refresh_token');
    apiClient.post('/auth/logout', { refresh_token: refreshToken }).catch(() => {});
    safeRemoveItem('access_token');
    safeRemoveItem('refresh_token');
    safeRemoveItem('current_user');
  },

  async verifyEmail(token: string): Promise<{ message: string; email: string }> {
    const response = await axios.get(`${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  async resendVerification(): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/resend-verification');
    return response.data;
  },

  async forgotPassword(email: string, org_code: string): Promise<any> {
    const response = await apiClient.post('/auth/forgot-password', { email, org_code });
    return response.data;
  },

  async resetPassword(token: string, new_password: string): Promise<any> {
    const response = await apiClient.post('/auth/reset-password', { token, new_password });
    return response.data;
  },
};

// Rooms API
export const roomsAPI = {
  async getAll(tenantId?: number | null): Promise<Room[]> {
    // tenantId = null means "All Tenants" for super admin
    // tenantId = undefined means no filter parameter sent
    // tenantId = number means filter by specific tenant
    const params: any = {};
    if (tenantId !== undefined && tenantId !== null) {
      params.tenant_id = tenantId;
    }
    // If tenantId is null (All Tenants), don't send tenant_id param - backend returns all
    const response = await apiClient.get('/rooms', { params });
    return response.data.data || response.data;
  },

  async getById(id: number): Promise<RoomDetail> {
    const response = await apiClient.get(`/rooms/${id}`);
    return response.data;
  },

  async create(room: RoomCreate, tenantId?: number): Promise<Room> {
    // Pass tenant_id as query parameter for super admin to assign room to tenant
    const url = tenantId ? `/rooms?tenant_id=${tenantId}` : '/rooms';
    const response = await apiClient.post(url, room);
    return response.data;
  },

  async update(id: number, room: Partial<RoomCreate>): Promise<Room> {
    const response = await apiClient.put(`/rooms/${id}`, room);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/rooms/${id}`);
  },
};

// Sensors API
export const sensorsAPI = {
  async getAll(roomId?: number, tenantId?: number): Promise<Sensor[]> {
    const params: any = {};
    if (roomId) params.room_id = roomId;
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get('/sensors', { params });
    return response.data.data || response.data;
  },

  async getById(id: number): Promise<SensorDetail> {
    const response = await apiClient.get(`/sensors/${id}`);
    return response.data;
  },

  async create(sensor: SensorCreate): Promise<Sensor> {
    const response = await apiClient.post('/sensors', sensor);
    return response.data;
  },

  async update(id: number, sensor: Partial<SensorCreate>): Promise<Sensor> {
    const response = await apiClient.put(`/sensors/${id}`, sensor);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/sensors/${id}`);
  },

  async getReadings(sensorId: number, hours: number = 24): Promise<SensorReading[]> {
    // Backend expects start_time/end_time, not hours
    const endTime = new Date().toISOString();
    const startTime = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

    // Use server-side aggregation for ranges where raw data exceeds 20K row limit.
    // Actual data rate is ~5K readings/day (30s intervals + duplicates), so:
    //   24h = ~5K raw (fits), 7d = ~35K (exceeds), 30d = ~150K, 3M = ~450K
    const params: Record<string, string | number> = { start_time: startTime, end_time: endTime };

    if (hours > 2160) {
      params.interval = '1day';       // 3M–12M → ~365 daily buckets
    } else if (hours > 720) {
      params.interval = '6hour';      // 1M–3M → ~360 buckets
    } else if (hours > 168) {
      params.interval = '2hour';      // 7d–30d → ~360 buckets
    } else if (hours > 24) {
      params.interval = '30min';      // 1d–7d → ~336 buckets
    } else {
      params.limit = 5000;            // ≤24h: raw data fits at ~5K/day
    }

    const response = await apiClient.get(`/readings/sensor/${sensorId}`, {
      params,
      timeout: 60000, // readings can be large — 60s timeout
    });
    return response.data.data || response.data;
  },

  async getLatestReading(sensorId: number): Promise<SensorReading | null> {
    const response = await apiClient.get(`/readings/sensor/${sensorId}/latest`);
    return response.data;
  },
};

// Alerts API
export const alertsAPI = {
  async getAll(roomId?: number, tenantId?: number, sensorId?: number, since?: string): Promise<Alert[]> {
    const params: any = {};
    if (roomId) params.room_id = roomId;
    if (tenantId) params.tenant_id = tenantId;
    if (sensorId) params.sensor_id = sensorId;
    if (since) params.since = since;
    params.limit = 500;
    const response = await apiClient.get('/alerts', { params });
    return response.data.data || response.data;
  },

  async acknowledge(id: number, data?: AlertAcknowledgeData): Promise<Alert> {
    const response = await apiClient.post(`/alerts/${id}/acknowledge`, data || null);
    return response.data;
  },

  async acknowledgeAll(): Promise<{ message: string; count: number }> {
    const response = await apiClient.post('/alerts/acknowledge-all');
    return response.data;
  },

  async resolve(id: number): Promise<Alert> {
    const response = await apiClient.post(`/alerts/${id}/resolve`);
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  async getAlertPreferences(): Promise<any[]> {
    const response = await apiClient.get('/settings/alert-preferences');
    return response.data;
  },

  async updateAlertPreference(id: number, data: any): Promise<any> {
    const response = await apiClient.put(`/settings/alert-preferences/${id}`, data);
    return response.data;
  },

  async createAlertPreference(data: any): Promise<any> {
    const response = await apiClient.post('/settings/alert-preferences', data);
    return response.data;
  },

  async getSystemSettings(): Promise<any[]> {
    const response = await apiClient.get('/settings/system');
    return response.data;
  },

  async updateSystemSetting(key: string, data: any): Promise<any> {
    const response = await apiClient.put(`/settings/system/${key}`, data);
    return response.data;
  },

  async updateProfile(data: any): Promise<any> {
    const response = await apiClient.put('/settings/profile', data);
    return response.data;
  },

  async changePassword(data: any): Promise<any> {
    const response = await apiClient.put('/settings/password', data);
    return response.data;
  },

  // Two-Factor Authentication (2FA)
  async get2FAStatus(): Promise<{ is_enabled: boolean; has_secret: boolean }> {
    const response = await apiClient.get('/settings/2fa/status');
    return response.data;
  },

  async setup2FA(): Promise<{ secret: string; provisioning_uri: string; message: string }> {
    const response = await apiClient.post('/settings/2fa/setup');
    return response.data;
  },

  async verify2FA(code: string): Promise<{ message: string }> {
    const response = await apiClient.post('/settings/2fa/verify', { code });
    return response.data;
  },

  async disable2FA(password: string): Promise<{ message: string }> {
    const response = await apiClient.post('/settings/2fa/disable', { password });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  async getAll(tenantId?: number | null): Promise<any[]> {
    // tenantId = null means "All Tenants" for super admin
    // tenantId = undefined means no filter parameter sent
    // tenantId = number means filter by specific tenant
    const params: any = {};
    if (tenantId !== undefined && tenantId !== null) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  async getById(id: number): Promise<any> {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  async create(userData: any): Promise<any> {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  async update(id: number, userData: any): Promise<any> {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },
};

// Logbook API
export const logbookAPI = {
  // Products
  async getProducts(): Promise<any[]> {
    const response = await apiClient.get('/logbook/products');
    return response.data;
  },

  async getProduct(id: number): Promise<any> {
    const response = await apiClient.get(`/logbook/products/${id}`);
    return response.data;
  },

  async createProduct(productData: any): Promise<any> {
    const response = await apiClient.post('/logbook/products', productData);
    return response.data;
  },

  async updateProduct(id: number, productData: any): Promise<any> {
    const response = await apiClient.put(`/logbook/products/${id}`, productData);
    return response.data;
  },

  async deleteProduct(id: number): Promise<void> {
    await apiClient.delete(`/logbook/products/${id}`);
  },

  // Storage Entries
  async getEntries(filters?: {
    status?: string;
    room_id?: number;
    product_id?: number;
    batch_number?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.room_id) params.append('room_id', filters.room_id.toString());
    if (filters?.product_id) params.append('product_id', filters.product_id.toString());
    if (filters?.batch_number) params.append('batch_number', filters.batch_number);

    const response = await apiClient.get(`/logbook/entries?${params.toString()}`);
    return response.data;
  },

  async getEntry(id: number): Promise<any> {
    const response = await apiClient.get(`/logbook/entries/${id}`);
    return response.data;
  },

  async createEntry(entryData: any): Promise<any> {
    const response = await apiClient.post('/logbook/entries', entryData);
    return response.data;
  },

  async updateEntry(id: number, entryData: any): Promise<any> {
    const response = await apiClient.put(`/logbook/entries/${id}`, entryData);
    return response.data;
  },

  async removeFromStorage(id: number, removalData: any): Promise<any> {
    const response = await apiClient.post(`/logbook/entries/${id}/remove`, removalData);
    return response.data;
  },

  async getEnvironmentalHistory(id: number): Promise<any> {
    const response = await apiClient.get(`/logbook/entries/${id}/environmental-history`);
    return response.data;
  },

  async getBatchReport(batchNumber: string): Promise<any> {
    const response = await apiClient.get(`/logbook/reports/batch/${batchNumber}`);
    return response.data;
  },

  async getDashboard(): Promise<any> {
    const response = await apiClient.get('/logbook/dashboard');
    return response.data;
  },
};

// Tenants API (Super Admin only)
export const tenantsAPI = {
  async getAll(): Promise<any[]> {
    const response = await apiClient.get('/tenants');
    return response.data;
  },

  async getActive(): Promise<any[]> {
    const response = await apiClient.get('/tenants/list/active');
    return response.data;
  },

  async getById(id: number): Promise<any> {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  async create(tenantData: any): Promise<any> {
    const response = await apiClient.post('/tenants', tenantData);
    return response.data;
  },

  async update(id: number, tenantData: any): Promise<any> {
    const response = await apiClient.put(`/tenants/${id}`, tenantData);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/tenants/${id}`);
  },

  async getStats(id: number): Promise<any> {
    const response = await apiClient.get(`/tenants/${id}/stats`);
    return response.data;
  },

  async updatePlan(id: number, planData: any): Promise<any> {
    const response = await apiClient.put(`/tenants/${id}/plan`, planData);
    return response.data;
  },

  // Tenant User Management
  async getUsers(tenantId: number): Promise<any[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/users`);
    return response.data;
  },

  async createUser(tenantId: number, userData: any): Promise<any> {
    const response = await apiClient.post(`/tenants/${tenantId}/users`, userData);
    return response.data;
  },

  async updateUser(tenantId: number, userId: number, userData: any): Promise<any> {
    const response = await apiClient.put(`/tenants/${tenantId}/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(tenantId: number, userId: number): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/users/${userId}`);
  },
};

// Devices API (Device Management, Activation Codes, Fleet Health)
export const devicesAPI = {
  // --- Activation Codes ---
  async getCodes(status?: string, tenantId?: number): Promise<ActivationCode[]> {
    const params: any = {};
    if (status) params.status = status;
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get('/device/codes', { params });
    return response.data;
  },

  async generateCodes(data: CodeGenerateRequest): Promise<{
    success: boolean;
    count: number;
    batch_id: string;
    codes: string[];
  }> {
    const response = await apiClient.post('/device/codes/generate', data);
    return response.data;
  },

  async revokeCode(codeId: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put(`/device/codes/${codeId}/revoke`);
    return response.data;
  },

  async bindDevice(codeId: number, expectedDeviceId: string): Promise<{
    success: boolean;
    code: string;
    expected_device_id: string;
    message: string;
  }> {
    const response = await apiClient.put(`/device/codes/${codeId}/bind`, {
      expected_device_id: expectedDeviceId,
    });
    return response.data;
  },

  getQRCodeUrl(codeId: number): string {
    return `${API_BASE_URL}/device/codes/${codeId}/qr`;
  },

  getStickersUrl(batchId: string): string {
    return `${API_BASE_URL}/device/codes/batch/${encodeURIComponent(batchId)}/stickers`;
  },

  // --- Device Fleet Health ---
  async getHealth(tenantId?: number): Promise<DeviceHealth[]> {
    const params: any = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get('/devices/health', { params });
    return response.data;
  },

  async checkHeartbeats(): Promise<HeartbeatResult> {
    const response = await apiClient.post('/devices/check-heartbeats');
    return response.data;
  },

  // --- Firmware Management ---
  async getFirmwareVersions(boardType?: string): Promise<FirmwareVersion[]> {
    const params: any = {};
    if (boardType) params.board_type = boardType;
    const response = await apiClient.get('/firmware/versions', { params });
    return response.data;
  },

  async uploadFirmware(formData: FormData): Promise<FirmwareVersion> {
    const response = await apiClient.post('/firmware/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minute timeout for large uploads
    });
    return response.data;
  },

  async markFirmwareStable(version: string): Promise<{ message: string; is_stable: boolean }> {
    const response = await apiClient.put(`/firmware/${version}/stable`);
    return response.data;
  },

  async deleteFirmware(version: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/firmware/${version}`);
    return response.data;
  },

  // --- Flash Logs ---
  async getFlashLogs(deviceId?: string, status?: string, limit?: number): Promise<FlashLog[]> {
    const params: any = {};
    if (deviceId) params.device_id = deviceId;
    if (status) params.status = status;
    if (limit) params.limit = limit;
    const response = await apiClient.get('/device/flash-logs', { params });
    return response.data;
  },

  // --- Device Lifecycle ---
  async replaceDevice(data: DeviceReplaceRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/replace', data);
    return response.data;
  },

  async relocateDevice(data: DeviceRelocateRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/relocate', data);
    return response.data;
  },

  async swapDevices(data: DeviceSwapRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/swap', data);
    return response.data;
  },

  async decommissionDevice(data: DeviceDecommissionRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/decommission', data);
    return response.data;
  },

  async reactivateDevice(data: DeviceReactivateRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/reactivate', data);
    return response.data;
  },

  async migrateData(data: DataMigrationRequest): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post('/device/lifecycle/migrate-data', data);
    return response.data;
  },

  async bulkReplace(data: { replacements: DeviceReplaceRequest[]; shared_reason?: string }): Promise<{ success: boolean; results: any[]; message: string }> {
    const response = await apiClient.post('/device/lifecycle/bulk-replace', data);
    return response.data;
  },

  async reverseEvent(eventId: number, reason: string): Promise<{ success: boolean; event: DeviceLifecycleEvent; message: string }> {
    const response = await apiClient.post(`/device/lifecycle/events/${eventId}/reverse`, { reason });
    return response.data;
  },

  async getLifecycleEvents(params?: {
    sensor_id?: number;
    room_id?: number;
    event_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ events: DeviceLifecycleEvent[]; total: number }> {
    const response = await apiClient.get('/device/lifecycle/events', { params });
    return response.data;
  },

  async getSensorHistory(sensorId: number): Promise<SensorHistory> {
    const response = await apiClient.get(`/device/lifecycle/sensor/${sensorId}/history`);
    return response.data;
  },

  async getComplianceReport(startDate: string, endDate: string): Promise<ComplianceReport> {
    const response = await apiClient.get('/device/lifecycle/compliance-report', {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  },

  // --- Activation Approval ---
  async getPendingActivations(): Promise<{ pending: PendingActivation[]; count: number }> {
    const response = await apiClient.get('/device/activation/pending');
    return response.data;
  },

  async approveActivation(sensorId: number, reason?: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/device/activation/${sensorId}/approve`, { reason });
    return response.data;
  },

  async rejectActivation(sensorId: number, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/device/activation/${sensorId}/reject`, { reason });
    return response.data;
  },

  // --- Audit Logs ---
  async getAuditLogs(params?: {
    action?: string;
    resource_type?: string;
    resource_id?: number;
    user_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: AuditLogEntry[]; total: number }> {
    const response = await apiClient.get('/audit-logs', { params });
    return response.data;
  },

  // --- Hashchain ---
  async getChainStatus(): Promise<ChainStatus> {
    const response = await apiClient.get('/audit/chain/status');
    return response.data;
  },

  async verifyChain(startSeq?: number, endSeq?: number): Promise<ChainVerifyResult> {
    const params: any = {};
    if (startSeq !== undefined) params.start_seq = startSeq;
    if (endSeq !== undefined) params.end_seq = endSeq;
    const response = await apiClient.post('/audit/chain/verify', null, { params });
    return response.data;
  },
};

// ============================================================================
// Phase 8: Analytics API
// ============================================================================
const analyticsAPI = {
  async getTemperatureTrends(params?: {
    room_id?: number;
    sensor_id?: number;
    interval?: string;
    period?: string;
  }) {
    const response = await apiClient.get('/analytics/temperature-trends', { params });
    return response.data;
  },

  async getExcursionSummary(params?: { room_id?: number; period?: string }) {
    const response = await apiClient.get('/analytics/excursion-summary', { params });
    return response.data;
  },

  async getSensorHealth(params?: { room_id?: number }) {
    const response = await apiClient.get('/analytics/sensor-health', { params });
    return response.data;
  },

  async getComplianceMetrics(params?: { room_id?: number; sensor_id?: number; period?: string }) {
    const response = await apiClient.get('/analytics/compliance-metrics', { params });
    return response.data;
  },
};

// ============================================================================
// Phase 8: Reports API
// ============================================================================
const reportsAPI = {
  async listReports(params?: {
    report_type?: string;
    format?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }) {
    const response = await apiClient.get('/reports', { params });
    return response.data;
  },

  async generateReport(params: {
    report_type: string;
    format?: string;
    room_id?: number;
    start_date?: string;
    end_date?: string;
  }) {
    const response = await apiClient.post('/reports/generate', null, { params });
    return response.data;
  },

  async getReport(reportId: number) {
    const response = await apiClient.get(`/reports/${reportId}`);
    return response.data;
  },
};

// ============================================================================
// Phase 8: Enhanced Alerts API (paginated)
// ============================================================================
const alertsPaginatedAPI = {
  async getPaginated(params?: {
    search?: string;
    severity?: string;
    is_acknowledged?: boolean;
    room_id?: number;
    sensor_id?: number;
    start_date?: string;
    end_date?: string;
    sort_by?: string;
    sort_order?: string;
    skip?: number;
    limit?: number;
  }) {
    const response = await apiClient.get('/alerts/paginated', { params });
    return response.data;
  },

  async bulkAcknowledge(alertIds: number[], corrective_action?: string, product_disposition?: string, notes?: string) {
    const response = await apiClient.post('/alerts/bulk-acknowledge', {
      alert_ids: alertIds,
      corrective_action: corrective_action || 'No action required',
      product_disposition: product_disposition || 'No impact',
      notes: notes || 'Bulk acknowledged',
    });
    return response.data;
  },
};

// ============================================================================
// Scheduler API
// ============================================================================
const schedulerAPI = {
  async getTasks(): Promise<{ data: any[]; total: number }> {
    const response = await apiClient.get('/scheduler/tasks');
    return response.data;
  },

  async updateTask(id: number, data: Record<string, any>): Promise<any> {
    // Convert config to JSON string if present
    const params: Record<string, any> = {};
    Object.entries(data).forEach(([key, val]) => {
      if (key === 'config' && typeof val === 'object') {
        params[key] = JSON.stringify(val);
      } else if (val !== undefined && val !== null) {
        params[key] = val;
      }
    });
    const response = await apiClient.put(`/scheduler/tasks/${id}`, null, { params });
    return response.data;
  },

  async deleteTask(id: number): Promise<{ message: string }> {
    const response = await apiClient.delete(`/scheduler/tasks/${id}`);
    return response.data;
  },

  async runTask(id: number): Promise<{ message: string; task_id: number }> {
    const response = await apiClient.post(`/scheduler/tasks/${id}/run`);
    return response.data;
  },

  async getExecutions(params?: {
    task_id?: number;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ data: any[]; total: number; skip: number; limit: number }> {
    const response = await apiClient.get('/scheduler/executions', { params });
    return response.data;
  },

  async getTaskExecutions(taskId: number, params?: {
    skip?: number;
    limit?: number;
  }): Promise<{ data: any[]; total: number }> {
    const response = await apiClient.get(`/scheduler/tasks/${taskId}/executions`, { params });
    return response.data;
  },

  async initDefaults(): Promise<{ data: any[]; message: string }> {
    const response = await apiClient.post('/scheduler/tasks/init-defaults');
    return response.data;
  },
};

// ============================================================================
// Two-Factor Authentication (2FA) API
// ============================================================================
export const twoFactorAPI = {
  async getStatus(): Promise<any> {
    const response = await apiClient.get('/2fa/status');
    return response.data;
  },
  async webauthnRegisterOptions(): Promise<any> {
    const response = await apiClient.post('/2fa/webauthn/register-options');
    return response.data;
  },
  async webauthnRegisterComplete(credential: any): Promise<any> {
    const response = await apiClient.post('/2fa/webauthn/register-complete', credential);
    return response.data;
  },
  async webauthnAuthOptions(tempToken: string): Promise<any> {
    const response = await apiClient.post('/2fa/webauthn/auth-options', {}, {
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    return response.data;
  },
  async webauthnAuthComplete(tempToken: string, credential: any, rememberDevice: boolean = false): Promise<any> {
    const response = await apiClient.post('/2fa/webauthn/auth-complete', {
      credential,
      remember_device: rememberDevice,
    }, {
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    return response.data;
  },
  async emailOtpSetup(): Promise<any> {
    const response = await apiClient.post('/2fa/email-otp/setup');
    return response.data;
  },
  async emailOtpSend(tempToken: string): Promise<any> {
    const response = await apiClient.post('/2fa/email-otp/send', {}, {
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    return response.data;
  },
  async emailOtpVerify(tempToken: string, code: string, rememberDevice: boolean = false): Promise<any> {
    const response = await apiClient.post('/2fa/email-otp/verify', {
      code,
      remember_device: rememberDevice,
    }, {
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    return response.data;
  },
  async deleteCredential(credentialId: number): Promise<any> {
    const response = await apiClient.delete(`/2fa/webauthn/credentials/${credentialId}`);
    return response.data;
  },
  async disable(password: string): Promise<any> {
    const response = await apiClient.post('/2fa/disable', { password });
    return response.data;
  },
};

// Gap Declarations API
const gapDeclarationsAPI = {
  async detect(roomId: number, startDate: string, endDate: string, thresholdMinutes: number = 30): Promise<GapDetectedItem[]> {
    const response = await apiClient.get('/gap-declarations/detect', {
      params: { room_id: roomId, start_date: startDate, end_date: endDate, threshold_minutes: thresholdMinutes },
    });
    return response.data;
  },
  async list(params?: { room_id?: number; status?: string; start_date?: string; end_date?: string }): Promise<GapDeclaration[]> {
    const response = await apiClient.get('/gap-declarations', { params });
    return response.data;
  },
  async get(id: number): Promise<GapDeclaration> {
    const response = await apiClient.get(`/gap-declarations/${id}`);
    return response.data;
  },
  async create(data: GapDeclarationCreate): Promise<GapDeclaration> {
    const response = await apiClient.post('/gap-declarations', data);
    return response.data;
  },
  async declare(id: number, data: GapDeclareRequest): Promise<GapDeclaration> {
    const response = await apiClient.put(`/gap-declarations/${id}/declare`, data);
    return response.data;
  },
  async approve(id: number, data?: GapApproveRequest): Promise<GapDeclaration> {
    const response = await apiClient.put(`/gap-declarations/${id}/approve`, data || {});
    return response.data;
  },
  async dismiss(id: number, data: GapDismissRequest): Promise<GapDeclaration> {
    const response = await apiClient.put(`/gap-declarations/${id}/dismiss`, data);
    return response.data;
  },
  async downloadCertificate(id: number): Promise<Blob> {
    const response = await apiClient.get(`/gap-declarations/${id}/certificate.pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Hub Gateway API
const hubsAPI = {
  list: (tenantId?: number) => apiClient.get('/hubs', { params: tenantId ? { tenant_id: tenantId } : {} }).then(r => r.data),
  get: (hubId: number) => apiClient.get(`/hubs/${hubId}`).then(r => r.data),
  create: (data: any) => apiClient.post('/hubs', data).then(r => r.data),
  update: (hubId: number, data: any) => apiClient.put(`/hubs/${hubId}`, data).then(r => r.data),
  delete: (hubId: number) => apiClient.delete(`/hubs/${hubId}`).then(r => r.data),
  pairSensor: (hubId: number, sensorId: number) => apiClient.post(`/hubs/${hubId}/pair`, { sensor_id: sensorId }).then(r => r.data),
  unpairSensor: (hubId: number, sensorId: number) => apiClient.post(`/hubs/${hubId}/unpair`, { sensor_id: sensorId }).then(r => r.data),
  getSensors: (hubId: number) => apiClient.get(`/hubs/${hubId}/sensors`).then(r => r.data),
  getHealth: (tenantId?: number) => apiClient.get('/hubs/health', { params: tenantId ? { tenant_id: tenantId } : {} }).then(r => r.data),
};

// Export default API object
const api = {
  auth: authAPI,
  rooms: roomsAPI,
  sensors: sensorsAPI,
  alerts: alertsAPI,
  alertsPaginated: alertsPaginatedAPI,
  settings: settingsAPI,
  users: usersAPI,
  logbook: logbookAPI,
  tenants: tenantsAPI,
  devices: devicesAPI,
  analytics: analyticsAPI,
  reports: reportsAPI,
  scheduler: schedulerAPI,
  twoFactor: twoFactorAPI,
  gapDeclarations: gapDeclarationsAPI,
  hubs: hubsAPI,
};

export { apiClient };
export default api;
